import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from './preload-generator.module.scss';

const PreloadGeneratorView = ({
  url,
  onUrlChange,
  blockOverride,
  onBlockOverrideChange,
  loading,
  error,
  result,
  outputText,
  onOutputTextChange,
  copied,
  onGenerate,
  onCopy,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      onGenerate();
    }
  };

  return (
    <div className={styles.page}>
      <Typography variant="h5" component="h1" gutterBottom>
        Preload Generator
      </Typography>
      <Box sx={{ mb: 3, color: 'text.secondary' }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Generate {'<link rel="preload">'} tags for Unbounce landing page hero images.
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Paste an Unbounce page URL and click <strong>Generate</strong>.</li>
            <li>Copy the output into the page{"'"}s custom <code>{'<head>'}</code> scripts in Unbounce.</li>
          </ol>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          The tool auto-detects the hero block by finding the image with the most
          responsive variants. If it picks the wrong block, expand{' '}
          <strong>Advanced</strong> and enter the block ID
          (e.g. <code>#lp-pom-block-317</code>) — find it by right-clicking the
          hero image in your browser and choosing <strong>Inspect</strong>.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label="Unbounce Page URL"
            placeholder="https://travel.zicasso.com/your-landing-page"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            size="small"
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={onGenerate}
            disabled={loading || !url.trim()}
            sx={{ minWidth: 120, height: 40 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate'}
          </Button>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Advanced
          </Button>
          <Collapse in={showAdvanced}>
            <TextField
              fullWidth
              label="Hero Block ID Override"
              placeholder="e.g. #lp-pom-block-317"
              value={blockOverride}
              onChange={(e) => onBlockOverrideChange(e.target.value)}
              disabled={loading}
              size="small"
              sx={{ mt: 1 }}
              helperText="Leave empty for auto-detection. Override if the wrong block is selected."
            />
          </Collapse>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Found {result.preloads.length} variant{result.preloads.length !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                {result.categories.map((cat) => (
                  <Chip key={cat} label={cat} size="small" variant="outlined" />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Hero block: {result.heroBlockId}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color={copied ? 'success' : 'secondary'}
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={onCopy}
              size="small"
              sx={{ minWidth: 120, flexShrink: 0 }}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </Button>
          </Box>

          <TextField
            fullWidth
            multiline
            value={outputText}
            onChange={(e) => onOutputTextChange(e.target.value)}
            variant="outlined"
            minRows={4}
            maxRows={20}
            InputProps={{
              sx: {
                fontFamily: '"Roboto Mono", monospace',
                fontSize: '0.78rem',
                lineHeight: 1.7,
              },
            }}
          />
        </Paper>
      )}
    </div>
  );
};

export default PreloadGeneratorView;
